import { useApiGetter, useApiEffect, AdminService } from '@localstack/services';
import { AdminPermission } from '@localstack/types';
import { ButtonWithDialog } from '@localstack/ui';
import { List, ListItemAvatar, ListItemText, Alert, Chip } from '@mui/material';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { AdminLayout } from '~/layouts';

export const FeatureFlags = (): JSX.Element => {
  const { can } = useAuthProvider();
  const { data: featureFlags, isLoading: isFeatureFlagsLoading } = useApiGetter(AdminService, 'listFeatureFlags', []);
  const { restartPlatformServices, isLoading: isPlatformRestarting } = useApiEffect(AdminService, [
    'restartPlatformServices',
  ]);

  const { toggleFeatureFlag, isLoading: isFeatureFlagsUpdating } = useApiEffect(AdminService, ['toggleFeatureFlag'], {
    revalidate: ['listFeatureFlags'],
  });

  return (
    <AdminLayout title="Feature Flags">
      <Alert
        severity="info"
        action={
          <ButtonWithDialog
            componentType="Button"
            color="primary"
            variant="outlined"
            size="small"
            disabled={isPlatformRestarting || !can(AdminPermission.FEATURE_FLAGS_WRITE)}
            dialogTitle="Restart All Platform Services?"
            onClick={restartPlatformServices}
            dialogContent={
              'Are you sure you want to restart All Platform Services? ' +
              'This action is typically safe because we use green-blue deployments. ' +
              'It will take a few minutes for the updated services to go live.'
            }
          >
            Restart
          </ButtonWithDialog>
        }
      >
        Feature flags supporting dynamic updates may take up to five minutes to refresh. If the state does not change
        consider restarting the platform.
      </Alert>
      <List>
        {(featureFlags ?? []).map(([name, description, path, state]) => (
          <ButtonWithDialog
            key={path}
            componentType="MenuItem"
            color="primary"
            disabled={isFeatureFlagsLoading || isFeatureFlagsUpdating || !can(AdminPermission.FEATURE_FLAGS_WRITE)}
            dialogTitle={`Toggle this feature state to ${state ? 'Off' : 'On'}`}
            onClick={() => toggleFeatureFlag({ path })}
            dialogContent="Selected instances will be permanently deleted"
          >
            <ListItemAvatar>
              {state ? (
                <Chip variant="filled" color="success" size="small" label="On" />
              ) : (
                <Chip variant="filled" color="error" size="small" label="Off" />
              )}
            </ListItemAvatar>
            <ListItemText primary={name} secondary={description} />
          </ButtonWithDialog>
        ))}
      </List>
    </AdminLayout>
  );
};
